import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('Verifying your email address, please wait...');
  const [authResponse, setAuthResponse] = useState(null);

  const hasRun = useRef(false);

  const handleStartJourney = () => {
    if (authResponse && authResponse.access_token) {
      loginWithToken(authResponse.access_token, authResponse.user);
      sessionStorage.setItem('rzHasLoggedIn', '1');
      navigate('/dashboard');
    }
  };

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const triggerVerification = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Missing verification token. Please verify the link in your email.');
        return;
      }

      try {
        const response = await api.get(`/auth/verify-email?token=${token}`);
        setStatus('success');
        setMessage(response.data.message || 'Email verified successfully!');
        setAuthResponse(response.data);
      } catch (err) {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Verification failed. The token may be invalid or expired.');
      }
    };

    triggerVerification();
  }, [token]);

  return (
    <div style={styles.page}>
      <div className="auth-blob-decoration" style={styles.blobTopRight} aria-hidden="true" />
      <div className="auth-blob-decoration" style={styles.blobBottomLeft} aria-hidden="true" />

      <main style={styles.card} className="glass-panel auth-card-container">
        <header style={styles.brandArea}>
          <span style={styles.logoMark} aria-hidden="true">⬡</span>
          <h1 style={styles.brandName}>RezFlow</h1>
        </header>

        <div style={styles.contentArea}>
          {status === 'verifying' && (
            <div style={styles.statusBox}>
              <div style={styles.spinner} />
              <p style={styles.text}>{message}</p>
            </div>
          )}

          {status === 'success' && (
            <div style={styles.statusBox}>
              <div style={styles.successIcon}>✓</div>
              <h2 style={styles.heading}>Email Verified! 🎉</h2>
              <p style={styles.subtext}>Your account is active. Let's start optimizing your first resume!</p>
              <button
                onClick={handleStartJourney}
                style={styles.btn}
                className="hover-lift"
              >
                Start Optimizing
              </button>
            </div>
          )}

          {status === 'error' && (
            <div style={styles.statusBox}>
              <div style={styles.errorIcon}>✗</div>
              <h2 style={styles.headingError}>Verification Failed</h2>
              <p style={styles.textError}>{message}</p>
              <Link to="/signup" style={styles.link}>
                Back to Sign Up
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100dvh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 20px',
    background: 'linear-gradient(145deg, #f0ede7 0%, #e8eee5 55%, #e7ecf0 100%)',
    position: 'relative',
    overflow: 'hidden',
  },
  blobTopRight: {
    position: 'absolute', top: '-120px', right: '-140px',
    width: '420px', height: '420px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(22,163,74,0.07) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  blobBottomLeft: {
    position: 'absolute', bottom: '-100px', left: '-120px',
    width: '360px', height: '360px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(35,43,34,0.06) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  card: {
    position: 'relative', zIndex: 1,
    width: '100%', maxWidth: '460px',
    borderRadius: 'var(--radius-2xl)',
    padding: '40px',
    textAlign: 'center',
    animation: 'rz-fadeIn 0.4s ease-out both',
  },
  brandArea: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: '10px', marginBottom: '30px',
  },
  logoMark: { fontSize: '24px', color: 'var(--color-charcoal-800)', lineHeight: 1 },
  brandName: {
    fontFamily: 'var(--font-heading)', fontSize: '28px',
    fontWeight: 700, letterSpacing: '-0.5px',
    color: 'var(--color-charcoal-900)', margin: 0,
  },
  contentArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  spinner: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    border: '3px solid rgba(79, 70, 229, 0.1)',
    borderTopColor: '#4f46e5',
    animation: 'rz-spin 0.8s linear infinite',
    marginBottom: '20px',
  },
  successIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    backgroundColor: 'rgba(79, 70, 229, 0.05)',
    color: '#4f46e5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '30px',
    fontWeight: 'bold',
    marginBottom: '20px',
    boxShadow: '0 4px 10px rgba(79, 70, 229, 0.15)',
  },
  errorIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    backgroundColor: '#FDEDEC',
    color: '#E74C3C',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '30px',
    fontWeight: 'bold',
    marginBottom: '20px',
    boxShadow: '0 4px 10px rgba(231, 76, 60, 0.15)',
  },
  heading: {
    fontSize: '22px',
    fontWeight: 'bold',
    color: '#2C3E50',
    margin: '0 0 10px 0',
  },
  headingError: {
    fontSize: '22px',
    fontWeight: 'bold',
    color: '#C0392B',
    margin: '0 0 10px 0',
  },
  text: {
    fontSize: '15px',
    color: '#5D6D7E',
    lineHeight: '1.5',
    margin: '0 0 15px 0',
  },
  textError: {
    fontSize: '15px',
    color: '#E74C3C',
    lineHeight: '1.5',
    margin: '0 0 20px 0',
  },
  subtext: {
    fontSize: '13px',
    color: '#7F8C8D',
    lineHeight: '1.5',
    margin: '0 0 25px 0',
  },
  btn: {
    width: '100%',
    padding: '14px 20px',
    backgroundColor: '#4f46e5',
    color: '#ffffff',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-heading)',
    fontWeight: 'bold',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)',
  },
  link: {
    color: 'var(--color-charcoal-800)',
    fontWeight: 'var(--font-weight-semibold)',
    textDecoration: 'none',
    borderBottom: '1px solid var(--color-border-strong)',
    transition: 'border-color var(--transition-fast)',
  },
};

export default VerifyEmail;
